import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { getCoachResourceRevision } from '@/lib/api';
import { continueResourceEditorCommand, dismissResourceEditorContinuation, loadResourceEditorContinuation,
  resourceEditorContinuationKind, restoreResourceEditorCommand } from '@/lib/resourceEditorContinuation';
import type { ResourceCommandResult, ResourceKind, ResourcePin } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';

/** Payload-free Library actions reuse the original editor journal and command
 * controller. The workspace host retains this owner outside row/modal lifetime. */
export function createResourceLibraryAction(context: DraftContext, entryId: string) {
  const scope = Object.freeze({ ...context });
  const kept = loadResourceEditorContinuation(scope, entryId);
  const original = kept?.pending ?? kept?.accepted?.command;
  if (kept && kept.return_target.kind !== 'standalone' || original && original.action !== 'archive' && original.action !== 'duplicate')
    throw new Error('Not a Library metadata action');
  let controller = kept?.pending ? restoreResourceEditorCommand(scope, entryId) : null;
  let receipt: ResourceCommandResult | null = kept?.pending ? null : kept?.accepted?.receipt ?? null;
  let action = original?.action ?? null;
  let kind: ResourceKind | null = kept ? resourceEditorContinuationKind(kept) : null;
  let disposed = false, departed = false, busy = false, version = 0;
  let error: string | null = null;
  let read: AbortController | null = null;
  const listeners = new Set<() => void>();
  const notify = () => { version++; listeners.forEach(listener => listener()); };
  const current = () => { if (!isCurrentDraftContext(scope)) departed = true; return !disposed && !departed; };
  const observe = () => { if (!current()) { read?.abort(); notify(); } };
  const authOff = useAuthStore.subscribe(observe), scopeOff = useScopeStore.subscribe(observe);
  const assertCurrent = () => { if (!current()) throw new DOMException('Resource context changed', 'AbortError'); };
  async function execute() {
    assertCurrent(); if (!controller || busy) throw new Error('Original Resource operation required');
    busy = true; error = null; notify();
    try {
      // Keep this exact controller even when its accepted receipt cannot yet be
      // persisted. send() then repairs the journal without another POST.
      const result = await controller.send(); assertCurrent();
      receipt = { ...result }; controller = null; return { ...result };
    } catch (cause) {
      if (current()) error = 'The original Resource result is not confirmed. Check the original operation.';
      throw cause;
    } finally { busy = false; notify(); }
  }
  return {
    entryId, scope,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    revision: () => version,
    getState() { return { current: current(), busy, error, action, kind, receipt: current() && receipt ? { ...receipt } : null,
      command: current() ? controller?.getState() ?? null : null }; },
    async start(nextAction: 'archive' | 'duplicate', pin: ResourcePin, expectedKind: ResourceKind) {
      assertCurrent();
      if (busy || controller || receipt) throw new Error('Original Resource operation required');
      const selected = { resource_id: pin.resource_id, revision_id: pin.revision_id };
      busy = true; error = null; notify(); read = new AbortController();
      try {
        const detail = await getCoachResourceRevision(selected, { scope, signal: read.signal }); assertCurrent();
        if (read.signal.aborted) throw new DOMException('Resource context changed', 'AbortError');
        const resource = detail.resource;
        if (resource.resource_id !== selected.resource_id || resource.revision_id !== selected.revision_id
          || resource.kind !== expectedKind || resource.archived) throw new Error('Resource selection unavailable');
        if (nextAction === 'archive' ? detail.editor?.can_archive !== true
          : detail.editor?.can_duplicate !== true || resource.availability !== 'available') throw new Error('Resource action unavailable');
        if (nextAction === 'duplicate' && resource.availability === 'available' && resource.kind === 'clip'
          && resource.provenance?.availability === 'unavailable') throw new Error('Resource provenance unavailable');
        action = nextAction; kind = expectedKind;
        const common = { operation_id: crypto.randomUUID(), expected_version: resource.version };
        const command = nextAction === 'archive'
          ? { action: nextAction, resourceId: selected.resource_id, body: { ...common, schema_version: 'resource-archive.v1' as const } }
          : { action: nextAction, resourceId: selected.resource_id, body: { ...common, schema_version: 'resource-duplicate.v1' as const, revision_id: selected.revision_id } };
        controller = continueResourceEditorCommand(scope, entryId, { kind: 'standalone' }, command,
          { kind, origin: { ...selected, version: resource.version } });
      } catch (cause) {
        if (current()) error = 'This Resource action is unavailable. The saved Resource is kept.';
        throw cause;
      } finally { busy = false; read = null; notify(); }
      return execute();
    },
    retry: execute,
    releaseRejected() {
      assertCurrent(); if (busy || controller?.getState().status !== 'rejected') throw new Error('Original Resource operation required');
      controller.releaseRejected(); controller = null; error = null; notify();
    },
    dismiss() {
      assertCurrent(); if (busy || controller) throw new Error('Original Resource operation required');
      dismissResourceEditorContinuation(scope, entryId); receipt = null; error = null; notify();
    },
    dispose() { disposed = true; read?.abort(); authOff(); scopeOff(); listeners.clear(); },
  };
}
export type ResourceLibraryAction = ReturnType<typeof createResourceLibraryAction>;
